import type { GetCnDataField } from '@/shared/DTO/GED';

/**
 * Detects which character types are allowed by a regex pattern.
 */
export function detectCharacterTypes(regex: string) {
    if (!regex) {
        return { hasUppercase: false, hasLowercase: false, hasDigits: false, hasSpecialChars: false };
    }

    const characterClassMatches = regex.match(/\[([^\]]+)\]/g) || [];

    let hasUppercase = false;
    let hasLowercase = false;
    let hasDigits = false;
    let hasSpecialChars = false;

    for (const charClass of characterClassMatches) {
        const content = charClass.slice(1, -1);
        const actualContent = content.startsWith('^') ? content.slice(1) : content;

        if (/A-Z/.test(actualContent)) hasUppercase = true;
        if (/a-z/.test(actualContent)) hasLowercase = true;
        if (/0-9/.test(actualContent) || /\\d/.test(actualContent)) hasDigits = true;
        if (/[A-Z]/.test(actualContent) && !/A-Z/.test(actualContent)) hasUppercase = true;
        if (/[a-z]/.test(actualContent) && !/a-z/.test(actualContent)) hasLowercase = true;
        if (/[0-9]/.test(actualContent) && !/0-9/.test(actualContent)) hasDigits = true;
        if (/[^A-Za-z0-9\\d\\s\\-]/.test(actualContent) || /\\W/.test(actualContent) || /\\p{P}/.test(actualContent) || /\\p{S}/.test(actualContent)) hasSpecialChars = true;
    }

    if (/\\p{Lu}/.test(regex)) hasUppercase = true;
    if (/\\p{Ll}/.test(regex)) hasLowercase = true;
    if (/\\d/.test(regex) && characterClassMatches.length === 0) hasDigits = true;
    if (/\\W/.test(regex) || /\\p{P}/.test(regex) || /\\p{S}/.test(regex)) hasSpecialChars = true;

    if (characterClassMatches.length === 0) {
        if (/[A-Z]/.test(regex) || /\\p{Lu}/.test(regex)) hasUppercase = true;
        if (/[a-z]/.test(regex) || /\\p{Ll}/.test(regex)) hasLowercase = true;
        if (/[0-9]/.test(regex) || /\\d/.test(regex) || /\\p{N}/.test(regex)) hasDigits = true;
    }

    return { hasUppercase, hasLowercase, hasDigits, hasSpecialChars };
}

/**
 * Extracts the maximum character count from a regex pattern.
 */
export function getMaxCharacterCount(regex: string): number | null {
    if (!regex) return null;

    const quantifierAfterClass = regex.match(/(?:\[[^\]]+\]|\([^)]+\)|\.)\{(\d+)(?:,(\d+)?)?\}/);
    if (quantifierAfterClass) {
        if (quantifierAfterClass[2]) {
            return parseInt(quantifierAfterClass[2], 10);
        } else if (quantifierAfterClass[1]) {
            const minMatch = regex.match(/(?:\[[^\]]+\]|\([^)]+\)|\.)\{(\d+),\}/);
            if (minMatch) return null;
            return parseInt(quantifierAfterClass[1], 10);
        }
    }

    const exactMatch = regex.match(/\{(\d+)\}/);
    const rangeMatch = regex.match(/\{(\d+),(\d+)\}/);

    if (exactMatch?.[1]) return parseInt(exactMatch[1], 10);
    if (rangeMatch?.[2]) return parseInt(rangeMatch[2], 10);

    return null;
}

/**
 * Returns true if the field renders as a dropdown (has multiple options to choose from).
 */
function isSelectField(field: GetCnDataField): boolean {
    return (field.options?.length ?? 0) > 1;
}

/**
 * Applies the same input transformation rules as AxFieldInputCn to a value.
 * - For SELECT fields: returns the value as-is (no transformation).
 * - For TEXT fields: strips invalid characters, applies case conversion and length limit.
 */
export function transformCnFieldValue(value: string, field: GetCnDataField): string {
    if (isSelectField(field)) {
        return value;
    }

    const { hasUppercase, hasLowercase, hasDigits } = detectCharacterTypes(field.regex);
    const onlyDigits = hasDigits && !hasUppercase && !hasLowercase;
    const upperOnly = hasUppercase && !hasLowercase;
    const lowerOnly = hasLowercase && !hasUppercase;

    let result = onlyDigits
        ? value.replace(/[^0-9]/g, '')
        : value.replace(/[^a-zA-Z]/g, '');

    if (upperOnly) result = result.toUpperCase();
    else if (lowerOnly) result = result.toLowerCase();

    const max = getMaxCharacterCount(field.regex);
    if (max !== null && result.length > max) {
        result = result.slice(0, max);
    }

    return result;
}

/**
 * Checks whether a value can be meaningfully pre-filled into a CN field.
 * - For SELECT fields: the value must exactly match one of the available options.
 * - For TEXT fields: the value must produce a non-empty result after transformation.
 */
export function canFitInCnField(value: string, field: GetCnDataField): boolean {
    if (!value) return false;

    if (isSelectField(field)) {
        return field.options?.includes(value) ?? false;
    }

    return transformCnFieldValue(value, field).length > 0;
}
